import { createSelector } from '@reduxjs/toolkit';

import type { QueuedEventModalSelection } from './types';
import { selectCohortBuilderState } from '../selectors/cohortBuilderEditModalSelectors';
import { selectCohortDefinition } from '../selectors/cohortDefinitionSelectors';

// ============================================================================
// Base Concept Group Selections Selectors
// ============================================================================

/**
 * Base selector for concept group selections (unified structure with domain context)
 * Returns null when feature flag is disabled or no selections available
 *
 * Part of the concept group selection implementation
 */
export const selectConceptGroupSelections = createSelector(
  selectCohortBuilderState,
  (cohortBuilderState) => cohortBuilderState?.conceptGroupSelections
);

/**
 * Selector for concept group selections with issues (warnings or errors)
 * Used to display warnings in the UI and generate suggested actions
 *
 * Part of the concept group selection implementation
 *
 * Includes selections with:
 * - status === 'warning' or 'error' AND !reported
 * - Error selections without valid concept groups should remain unreviewed even after follow-up requests
 */
export const selectConceptGroupSelectionsWithIssues = createSelector(
  selectConceptGroupSelections,
  (selections) => {
    if (!selections) return [];

    return selections.filter((selection) => {
      // Must have warning or error status
      if (selection.status !== 'warning' && selection.status !== 'error') {
        return false;
      }

      // Must not be reported (reviewed)
      if (selection.reported) {
        return false;
      }

      // Error selections without valid concept groups are considered to have issues
      // and should remain unreviewed until resolved
      return true;
    });
  }
);

/**
 * Selector for concept group selections by container (event or demographic)
 * Useful for displaying selections specific to a particular event or criterion
 *
 * Part of the concept group selection implementation
 */
export const selectConceptGroupSelectionsByContainer = (containerName: string) =>
  createSelector(selectConceptGroupSelections, (selections) => {
    if (!selections || !containerName) return [];

    const normalizedName = containerName.toLowerCase();
    return selections.filter(
      (selection) => selection.containerName?.toLowerCase() === normalizedName
    );
  });

/**
 * Selector to check if there are any unresolved selections (warnings/errors)
 * Used for validation and UI indicators
 *
 * Part of the concept group selection implementation
 */
export const selectHasUnresolvedConceptGroupSelections = createSelector(
  selectConceptGroupSelectionsWithIssues,
  (selectionsWithIssues) => selectionsWithIssues.length > 0
);

// ============================================================================
// Transformation Selectors (extracted from hooks)
// ============================================================================

/**
 * Selector for concept group selections filtered by span name (case-insensitive)
 * Used to find selections matching a specific concept group name
 *
 * Part of the concept group selection implementation
 */
export const selectConceptGroupSelectionsBySpan = (span: string) =>
  createSelector(selectConceptGroupSelections, (selections) => {
    if (!selections || !span) return [];

    const normalizedSpan = span.trim().toLowerCase();
    return selections.filter((selection) => selection.span.trim().toLowerCase() === normalizedSpan);
  });

/**
 * Selector for concept group selection by filter_key (exact match)
 * Used for precise matching after POST creation
 *
 * Part of the concept group selection implementation
 */
export const selectConceptGroupSelectionsByFilterKey = (filterKey: string) =>
  createSelector(selectConceptGroupSelections, (selections) => {
    if (!selections || !filterKey) return null;

    return selections.find((selection) => selection.filterKey === filterKey) || null;
  });

/**
 * Selector to map container name to event ID from cohort definition
 * Searches in simpleEvents, derivedEvents, and subjectVariables
 *
 * Part of the concept group selection implementation
 */
export const selectEventIdFromContainerName = (containerName: string) =>
  createSelector(selectCohortDefinition, (cohortDefinition) => {
    if (!cohortDefinition || !containerName) return null;

    const normalizedName = containerName.toLowerCase();

    // Search in simpleEvents
    const simpleEvent = cohortDefinition.simpleEvents?.find(
      (e) => e.name?.toLowerCase() === normalizedName
    );
    if (simpleEvent) return simpleEvent.id;

    // Search in derivedEvents
    const derivedEvent = cohortDefinition.derivedEvents?.find(
      (e) => e.name?.toLowerCase() === normalizedName
    );
    if (derivedEvent) return derivedEvent.id;

    // Search in subjectVariables (demographics)
    const subjectVariable = cohortDefinition.subjectVariables?.find(
      (sv) => sv.name?.toLowerCase() === normalizedName
    );
    if (subjectVariable) return subjectVariable.id;

    // If not found, return null (shouldn't happen in normal flow)
    return null;
  });

/**
 * Composite selector that transforms concept group selections to queued event modal selections
 * This selector:
 * 1. Filters selections by concept group name (span)
 * 2. Maps container names to event IDs
 * 3. Transforms to QueuedEventModalSelection[]
 * 4. Deduplicates by filterKey
 *
 * Part of the concept group selection implementation
 */
export const selectQueuedEventModalSelections = (conceptGroupName: string) =>
  createSelector(
    selectConceptGroupSelectionsBySpan(conceptGroupName),
    selectCohortDefinition,
    (matchingSelections, cohortDefinition): QueuedEventModalSelection[] => {
      if (!matchingSelections.length || !cohortDefinition) return [];

      // Helper function to map container name to event ID
      const getEventIdFromName = (containerName: string | undefined | null): string | null => {
        if (!containerName) return null;

        const normalizedName = containerName.toLowerCase();

        // Search in simpleEvents
        const simpleEvent = cohortDefinition.simpleEvents?.find(
          (e) => e.name?.toLowerCase() === normalizedName
        );
        if (simpleEvent) return simpleEvent.id;

        // Search in derivedEvents
        const derivedEvent = cohortDefinition.derivedEvents?.find(
          (e) => e.name?.toLowerCase() === normalizedName
        );
        if (derivedEvent) return derivedEvent.id;

        // Search in subjectVariables (demographics)
        const subjectVariable = cohortDefinition.subjectVariables?.find(
          (sv) => sv.name?.toLowerCase() === normalizedName
        );
        if (subjectVariable) return subjectVariable.id;

        // If not found, return null
        return null;
      };

      // Map selections to QueuedEventModalSelection format
      // Filter out selections with missing containerName before mapping
      const selections: QueuedEventModalSelection[] = matchingSelections
        .filter((selection) => selection.containerName != null)
        .map((selection) => {
          const eventId = getEventIdFromName(selection.containerName);
          if (!eventId) return null;

          return {
            eventId,
            filterKey: selection.filterKey,
            filterType: selection.filterType,
            attribute: selection.attribute,
          };
        })
        .filter((s): s is QueuedEventModalSelection => s !== null);

      // Deduplicate by filterKey to avoid queueing same filter multiple times
      const uniqueSelections = Array.from(
        new Map(selections.map((s) => [s.filterKey, s])).values()
      );

      return uniqueSelections;
    }
  );

/**
 * Selector that prepares concept group selections data for suggested actions
 * Returns selections with necessary metadata for action generation
 *
 * Part of the concept group selection implementation
 */
export const selectConceptGroupSelectionsForSuggestedActions = createSelector(
  selectConceptGroupSelectionsWithIssues,
  (selections) => {
    if (!selections) return [];

    return selections.map((selection) => ({
      filterKey: selection.filterKey,
      span: selection.span,
      containerName: selection.containerName,
      status: selection.status,
    }));
  }
);
